import { Loader2, Plus, Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "sonner";

import { SocialPostPreview } from "@/components/campaign/social-post-preview";
import { resolveAssistantSelectedAssetPreview } from "@/lib/assistant-selected-asset-preview";
import {
  isAssistantStandaloneCarouselRequest,
  type AssistantStandaloneAssetBatchPreviewReference,
} from "@/lib/assistant-standalone-asset-batch-previews";
import { resolveChatGeneratedAssetAspectRatio } from "@/lib/chat-generated-media-aspect-ratio";
import { trpc } from "@/lib/trpc";
import { Button } from "@runcheetah/ui";
import type { ChatGeneratedAsset } from "./assistant-chat-media-gallery-types";

import {
  getProposalStorageKey,
  loadProposalState,
  saveProposalState,
  shouldPollCreatedBatchStatus,
} from "./assistant-standalone-asset-batch-preview-view-model";

export function AssistantStandaloneAssetBatchPreviews({
  brandId,
  sourceChatId,
  references,
  isStale = false,
  upgradeLabel,
  onUpgradeRequired,
  onUseAssetInChat,
}: {
  brandId: string;
  sourceChatId?: string | null;
  references: AssistantStandaloneAssetBatchPreviewReference[];
  isStale?: boolean;
  upgradeLabel?: string;
  onUpgradeRequired?: () => void;
  onUseAssetInChat?: (asset: ChatGeneratedAsset) => void;
}) {
  if (references.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {references.map((reference) => (
        <AssistantStandaloneAssetBatchPreviewBanner
          key={reference.id}
          brandId={brandId}
          sourceChatId={sourceChatId}
          reference={reference}
          isStale={isStale}
          upgradeLabel={upgradeLabel}
          onUpgradeRequired={onUpgradeRequired}
          onUseAssetInChat={onUseAssetInChat}
        />
      ))}
    </div>
  );
}

function AssistantStandaloneAssetBatchPreviewBanner({
  brandId,
  sourceChatId,
  reference,
  isStale,
  upgradeLabel,
  onUpgradeRequired,
  onUseAssetInChat,
}: {
  brandId: string;
  sourceChatId?: string | null;
  reference: AssistantStandaloneAssetBatchPreviewReference;
  isStale: boolean;
  upgradeLabel?: string;
  onUpgradeRequired?: () => void;
  onUseAssetInChat?: (asset: ChatGeneratedAsset) => void;
}) {
  const intl = useIntl();
  const isCarouselRequest = isAssistantStandaloneCarouselRequest(reference.request);
  const utils = trpc.useUtils();
  const proposalStorageKey = getProposalStorageKey(brandId, reference.id);
  const [proposalState, setProposalState] = useState(() => loadProposalState(proposalStorageKey));
  const [error, setError] = useState<string | null>(null);
  const cancelled = proposalState.status === "cancelled";
  const completed = proposalState.status === "completed";
  const createdBatchId = proposalState.status === "created" ? proposalState.batchId : null;
  const completedBatchId =
    proposalState.status === "completed" ? (proposalState.batchId ?? null) : null;
  const activeBatchId = createdBatchId ?? completedBatchId;
  const closed = proposalState.status === "active" && isStale;
  const persistedRunsRequired =
    proposalState.status === "created" || proposalState.status === "completed"
      ? (proposalState.runsRequired ?? null)
      : null;
  const quoteQuery = trpc.standaloneAssetBatch.quoteCreate.useQuery(
    { brandId, request: reference.request },
    { enabled: !closed && !cancelled && !createdBatchId, retry: false },
  );
  const createdBatchQuery = trpc.standaloneAssetBatch.get.useQuery(
    { brandId, id: createdBatchId ?? "00000000-0000-4000-8000-000000000000" },
    {
      enabled: Boolean(createdBatchId),
      refetchInterval: (query) =>
        shouldPollCreatedBatchStatus(query.state.data?.status) ? 5_000 : false,
    },
  );
  const generatedAssetsQuery = trpc.brandAssistantChat.generatedAssets.useQuery(
    { brandId, chatId: sourceChatId ?? "00000000-0000-4000-8000-000000000000" },
    {
      enabled: Boolean(sourceChatId && activeBatchId),
      refetchInterval: createdBatchId ? 5_000 : false,
    },
  );
  const createBatch = trpc.standaloneAssetBatch.create.useMutation({
    onSuccess: async (data) => {
      const runsRequired = quoteQuery.data?.runsRequired;
      const nextState = {
        status: "created" as const,
        batchId: data.id,
        ...(typeof runsRequired === "number" ? { runsRequired } : {}),
      };
      saveProposalState(proposalStorageKey, nextState);
      setProposalState(nextState);
      setError(null);
      const invalidations = [
        utils.standaloneAssetBatch.list.invalidate({ brandId }),
        utils.brand.getBillingOverview.invalidate({ brandId }),
      ];
      if (sourceChatId) {
        invalidations.push(
          utils.brandAssistantChat.generatedAssets.invalidate({ brandId, chatId: sourceChatId }),
        );
      }
      await Promise.all(invalidations);
      toast.success(
        intl.formatMessage(
          isCarouselRequest
            ? {
                id: "assistantAssetBatchPreview.createCarouselSuccess",
                defaultMessage: "Carousel generation started",
              }
            : {
                id: "assistantAssetBatchPreview.createSuccess",
                defaultMessage: "Asset batch generation started",
              },
        ),
      );
    },
    onError: (mutationError) => setError(mutationError.message),
  });

  const quote = quoteQuery.data ?? null;
  const hasEnoughRuns =
    quote?.runsRemaining === null ||
    quote?.runsRequired === undefined ||
    quote.runsRemaining >= quote.runsRequired;
  const canConfirm = Boolean(
    quote && hasEnoughRuns && !closed && !cancelled && !completed && !createdBatchId,
  );
  const createdBatchFailed = createdBatchQuery.data?.status === "failed";

  useEffect(() => {
    if (!createdBatchId) {
      return;
    }

    const generatedAssetsBatch = generatedAssetsQuery.data?.batches.find(
      (batch) => batch.id === createdBatchId,
    );
    const createdBatchGenerated = createdBatchQuery.data?.status === "generated";
    const generatedAssetsBatchGenerated = generatedAssetsBatch?.status === "generated";

    if (!createdBatchGenerated && !generatedAssetsBatchGenerated) {
      return;
    }

    const nextState = {
      status: "completed" as const,
      batchId: createdBatchId,
      ...(typeof persistedRunsRequired === "number" ? { runsRequired: persistedRunsRequired } : {}),
    };
    saveProposalState(proposalStorageKey, nextState);
    setProposalState(nextState);
  }, [
    createdBatchId,
    createdBatchQuery.data?.status,
    generatedAssetsQuery.data?.batches,
    persistedRunsRequired,
    proposalStorageKey,
  ]);

  const confirm = () => {
    if (!canConfirm || createBatch.isPending) {
      return;
    }

    createBatch.mutate({
      brandId,
      request: reference.request,
      sourceChatId: sourceChatId ?? undefined,
    });
  };
  const generatedAssets = activeBatchId
    ? (generatedAssetsQuery.data?.assets.filter((asset) => asset.batchId === activeBatchId) ?? [])
    : [];

  if (cancelled) {
    return (
      <div className="rounded-3xl border border-border/70 bg-muted/35 px-4 py-3 text-sm text-muted-foreground">
        {isCarouselRequest ? (
          <FormattedMessage
            id="assistantAssetBatchPreview.cancelledCarousel"
            defaultMessage="Carousel proposal cancelled."
          />
        ) : (
          <FormattedMessage
            id="assistantAssetBatchPreview.cancelled"
            defaultMessage="Asset batch proposal cancelled."
          />
        )}
      </div>
    );
  }

  return (
    <article className="border-y border-border/60 bg-transparent">
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground/88">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              {isCarouselRequest ? (
                <FormattedMessage
                  id="assistantAssetBatchPreview.titleCarousel"
                  defaultMessage="Generate Carousel?"
                />
              ) : (
                <FormattedMessage
                  id="assistantAssetBatchPreview.title"
                  defaultMessage="Generate Asset Batch?"
                />
              )}
            </div>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              {reference.summary ?? reference.request.prompt}
            </p>
          </div>
        </div>

        {!closed && !completed && !createdBatchId && !hasEnoughRuns && quote ? (
          <div className="rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <FormattedMessage
              id="assistantAssetBatchPreview.insufficientRuns"
              defaultMessage="This needs {required} runs, but only {remaining} remain."
              values={{ required: quote.runsRequired, remaining: quote.runsRemaining ?? 0 }}
            />
          </div>
        ) : null}
        {!closed && !completed && !createdBatchId && (error ?? quoteQuery.error?.message) ? (
          <div className="rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error ?? quoteQuery.error?.message}
          </div>
        ) : null}

        {closed ? (
          <div className="border-t border-border/60 pt-3 text-sm text-muted-foreground">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <X className="text-muted-foreground/40" size="16" aria-hidden="true" />
              <span className="font-medium text-foreground/58">
                <FormattedMessage
                  id="assistantAssetBatchPreview.supersededLabel"
                  defaultMessage="This proposal can no longer be accepted because the conversation has moved forward."
                />
              </span>
            </div>
          </div>
        ) : createdBatchId ? (
          <div className="border-t border-border/60 pt-3 text-sm text-muted-foreground">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {createdBatchFailed ? (
                <>
                  <X className="text-destructive/60" size="16" aria-hidden="true" />
                  <span className="font-medium text-foreground/58">
                    <FormattedMessage
                      id="assistantAssetBatchPreview.failedLabel"
                      defaultMessage="Failed"
                    />
                    :
                  </span>
                  <span className="min-w-0 truncate text-destructive/80">
                    {isCarouselRequest ? (
                      <FormattedMessage
                        id="assistantAssetBatchPreview.failedCarousel"
                        defaultMessage="Carousel generation failed"
                      />
                    ) : (
                      <FormattedMessage
                        id="assistantAssetBatchPreview.failedBatch"
                        defaultMessage="Asset batch generation failed"
                      />
                    )}
                  </span>
                </>
              ) : (
                <>
                  <Sparkles className="text-muted-foreground/40" size="16" aria-hidden="true" />
                  <span className="min-w-0 truncate bg-[linear-gradient(90deg,var(--muted-foreground)_0%,var(--muted-foreground)_34%,var(--primary)_50%,var(--muted-foreground)_66%,var(--muted-foreground)_100%)] bg-[length:220%_100%] bg-clip-text text-transparent motion-safe:animate-[assistant-status-aurora_2.2s_linear_infinite]">
                    {isCarouselRequest ? (
                      <FormattedMessage
                        id="assistantAssetBatchPreview.generatingCarousel"
                        defaultMessage="Generating Carousel"
                      />
                    ) : (
                      <FormattedMessage
                        id="assistantAssetBatchPreview.generatingAssets"
                        defaultMessage="{count, plural, one {Generating # asset} other {Generating # assets}}"
                        values={{ count: reference.request.items.length }}
                      />
                    )}
                  </span>
                  {typeof persistedRunsRequired === "number" ? (
                    <>
                      <span
                        className="h-1 w-1 rounded-full bg-muted-foreground/42"
                        aria-hidden="true"
                      />
                      <span className="font-medium text-foreground/58">
                        <FormattedMessage
                          id="assistantAssetBatchPreview.runsUsed"
                          defaultMessage="{count, plural, =0 {Free} one {# run used} other {# runs used}}"
                          values={{ count: persistedRunsRequired }}
                        />
                      </span>
                    </>
                  ) : null}
                </>
              )}
            </div>
          </div>
        ) : completed ? (
          <div className="border-t border-border/60 pt-3 text-sm text-muted-foreground">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <Sparkles className="text-muted-foreground/40" size="16" aria-hidden="true" />
              <span className="min-w-0 text-muted-foreground">
                {isCarouselRequest ? (
                  <FormattedMessage
                    id="assistantAssetBatchPreview.generatedCarousel"
                    defaultMessage="Carousel Generated"
                  />
                ) : (
                  <FormattedMessage
                    id="assistantAssetBatchPreview.generatedAssets"
                    defaultMessage="{count, plural, one {Generated # asset} other {Generated # assets}}"
                    values={{ count: reference.request.items.length }}
                  />
                )}
              </span>
              {typeof persistedRunsRequired === "number" ? (
                <>
                  <span
                    className="h-1 w-1 rounded-full bg-muted-foreground/42"
                    aria-hidden="true"
                  />
                  <span className="font-medium text-foreground/58">
                    <FormattedMessage
                      id="assistantAssetBatchPreview.runsUsed"
                      defaultMessage="{count, plural, =0 {Free} one {# run used} other {# runs used}}"
                      values={{ count: persistedRunsRequired }}
                    />
                  </span>
                </>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="border-t border-border/60 pt-3">
            <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                className="rounded-full text-foreground/65"
                disabled={createBatch.isPending}
                onClick={() => {
                  saveProposalState(proposalStorageKey, { status: "cancelled" });
                  setProposalState({ status: "cancelled" });
                }}
              >
                <X className="h-4 w-4" aria-hidden="true" />
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              {!hasEnoughRuns && onUpgradeRequired ? (
                <Button type="button" className="rounded-full" onClick={onUpgradeRequired}>
                  {upgradeLabel ?? (
                    <FormattedMessage id="runConfirmation.upgrade" defaultMessage="Upgrade" />
                  )}
                </Button>
              ) : (
                <Button
                  type="button"
                  className="rounded-full"
                  disabled={!canConfirm || createBatch.isPending}
                  onClick={confirm}
                >
                  {createBatch.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  {quote && isCarouselRequest ? (
                    <FormattedMessage
                      id="assistantAssetBatchPreview.confirmCarouselWithRuns"
                      defaultMessage="Generate Carousel for {runCount, plural, =0 {free} one {# run} other {# runs}}"
                      values={{ runCount: quote.runsRequired }}
                    />
                  ) : quote ? (
                    <FormattedMessage
                      id="assistantAssetBatchPreview.confirmWithRuns"
                      defaultMessage="Generate Asset Batch for {runCount, plural, =0 {free} one {# run} other {# runs}}"
                      values={{ runCount: quote.runsRequired }}
                    />
                  ) : isCarouselRequest ? (
                    <FormattedMessage
                      id="assistantAssetBatchPreview.confirmCarousel"
                      defaultMessage="Generate Carousel"
                    />
                  ) : (
                    <FormattedMessage
                      id="assistantAssetBatchPreview.confirm"
                      defaultMessage="Generate Asset Batch"
                    />
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
        {generatedAssets.length > 0 ? (
          <GeneratedAssetResultGrid assets={generatedAssets} onUseAssetInChat={onUseAssetInChat} />
        ) : null}
      </div>
    </article>
  );
}

function GeneratedAssetResultGrid({
  assets,
  onUseAssetInChat,
}: {
  assets: ChatGeneratedAsset[];
  onUseAssetInChat?: (asset: ChatGeneratedAsset) => void;
}) {
  return (
    <div className="border-t border-border/60 pt-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {assets.map((asset) => {
          const aspectRatio = resolveChatGeneratedAssetAspectRatio(asset);
          const preview = resolveAssistantSelectedAssetPreview({ asset });

          return (
            <div
              key={asset.generatedAssetId}
              className="group relative overflow-hidden rounded-2xl bg-muted shadow-sm"
              style={{ aspectRatio }}
            >
              {preview?.plan ? (
                <SocialPostPreview
                  renderMode="preview"
                  platform={preview.plan.platform}
                  type={preview.plan.postType}
                  plan={preview.plan}
                  linkedAssets={preview.linkedAssets}
                  brandkit={preview.brandkit}
                  showMetadata={false}
                  fillContainer="cover"
                  className="h-full w-full transition duration-300 group-hover:scale-[1.03]"
                />
              ) : (
                <img
                  src={asset.signedUrl}
                  alt={asset.caption ?? asset.prompt ?? "Generated asset"}
                  className="h-full w-full object-contain transition duration-300 group-hover:scale-[1.03]"
                  loading="lazy"
                />
              )}
              {onUseAssetInChat ? (
                <button
                  type="button"
                  className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-background/78 px-2.5 py-1.5 text-[11px] font-bold text-foreground opacity-0 shadow-lg shadow-black/15 backdrop-blur transition hover:bg-background group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
                  onClick={() => onUseAssetInChat(asset)}
                >
                  <Plus className="h-3.5 w-3.5 text-primary" aria-hidden />
                  <FormattedMessage
                    id="pulse.mediaGallery.addToTask"
                    defaultMessage="Add to task"
                  />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
